export type EnrichedResponse = Response & {
  __bodyText?: string;
  __bodyJson?: unknown;
};

export type FetchAdapterWithPreconnect = (
  input: RequestInfo | URL,
  init?: RequestInit,
) => Promise<EnrichedResponse>;

export interface SandboxEnvironmentOptions {
  fetchAdapter?: FetchAdapterWithPreconnect;
}

export const DSLM_HEADERS_INTERNAL_KEY = "__dslmHeaders__";

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object";

const toUrlString = (value: unknown): string | undefined => {
  if (typeof value === "string" && value.length > 0) {
    return value;
  }

  if (isPlainObject(value)) {
    const record = value as Record<string, unknown>;
    const direct = record["url"] ?? record["href"];
    if (typeof direct === "string" && direct.length > 0) {
      return direct;
    }

    if (direct && typeof direct === "object") {
      return toUrlString(direct);
    }
  }

  if (
    value &&
    typeof value === "object" &&
    typeof (value as { toString?: () => unknown }).toString === "function"
  ) {
    try {
      const text = (value as { toString: () => unknown }).toString();
      if (typeof text === "string" && text.length > 0) {
        return text;
      }
    } catch {
      // Ignore custom toString errors, caller will fall back to default handling.
    }
  }

  return undefined;
};

const resolveResponseBodies = async (response: Response) => {
  const textPromise = response
    .clone()
    .text()
    .catch(() => undefined);
  const jsonPromise = response
    .clone()
    .json()
    .catch(() => undefined);
  const [bodyText, bodyJson] = await Promise.all([textPromise, jsonPromise]);

  return { bodyText, bodyJson } as const;
};

const normalizeHeadersInitValue = (value: unknown): HeadersInit | undefined => {
  if (!value) {
    return undefined;
  }

  if (typeof Headers !== "undefined" && value instanceof Headers) {
    return value;
  }

  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    const raw = record[DSLM_HEADERS_INTERNAL_KEY];
    if (raw && typeof raw === "object") {
      return { ...(raw as Record<string, string>) };
    }
  }

  return value as HeadersInit;
};

const normalizeFetchParams = (
  input: RequestInfo | URL,
  init?: RequestInit,
): { input: RequestInfo | URL; init?: RequestInit } => {
  const assignHeaders = (target?: RequestInit): RequestInit | undefined => {
    if (!target || !target.headers) {
      return target;
    }
    const normalizedHeaders = normalizeHeadersInitValue(target.headers);
    if (normalizedHeaders) {
      target.headers = normalizedHeaders;
    } else {
      delete target.headers;
    }
    return target;
  };

  const createResult = (
    normalizedInput: RequestInfo | URL,
    normalizedInit?: RequestInit,
  ) => {
    const result: { input: RequestInfo | URL; init?: RequestInit } = {
      input: normalizedInput,
    };
    if (normalizedInit) {
      result.init = normalizedInit;
    }
    return result;
  };

  const normalizedInit = init ? assignHeaders({ ...init }) : undefined;
  const isRequestInstance =
    typeof Request !== "undefined" && input instanceof Request;
  const isUrlInstance = typeof URL !== "undefined" && input instanceof URL;

  if (typeof input === "string" || isUrlInstance || isRequestInstance) {
    return createResult(input, normalizedInit);
  }

  if (isPlainObject(input)) {
    const record = input as Record<string, unknown>;
    const url = record["url"];
    const href = record["href"];
    const restEntries = Object.entries(record).filter(
      ([key]) => key !== "url" && key !== "href",
    );
    const rest = Object.fromEntries(restEntries) as RequestInit;
    const resolvedUrl =
      toUrlString(url ?? href) ?? toUrlString(input) ?? undefined;
    if (resolvedUrl) {
      const mergedInit = assignHeaders({ ...rest, ...(normalizedInit ?? {}) });
      return createResult(resolvedUrl, mergedInit);
    }
  }

  return createResult(String(input ?? ""), normalizedInit);
};

/**
 * Creates a fetch adapter compatible with QuickJS sandboxes that enriches responses with
 * pre-parsed body helpers. The adapter works in both browser and Deno environments.
 */
export const createFetchAdapter = (): FetchAdapterWithPreconnect => {
  return async (input: RequestInfo | URL, init?: RequestInit) => {
    const { input: normalizedInput, init: normalizedInit } =
      normalizeFetchParams(input, init);
    const mapped = await fetch(normalizedInput, normalizedInit);
    const { bodyText, bodyJson } = await resolveResponseBodies(mapped);
    const enriched = mapped as EnrichedResponse;
    if (typeof bodyText === "string") {
      enriched.__bodyText = bodyText;
    } else {
      delete enriched.__bodyText;
    }
    if (bodyJson !== undefined) {
      enriched.__bodyJson = bodyJson;
    } else {
      delete enriched.__bodyJson;
    }

    return enriched;
  };
};

/**
 * Normalizes the value returned by the sandbox so that both the Edge function and
 * the browser UI show consistent information.
 */
export const formatSandboxOutput = (value: unknown): string => {
  if (value === undefined || value === null) {
    return "(no return value)";
  }

  if (typeof value === "object") {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }

  return String(value);
};
